import { useCallback, useState } from "react";

import { useTranslation } from "next-i18next";

import Icon from "@components/Icon";
import { useMessage } from "@components/Message";
import useNonce from "@hooks/useNonce";
import scrollToTheTop from "@lib/scrollToTheTop";
import debounce from "@utils/debounce";

import { OrderFields, ORDER_MESSAGE_ZONE } from "../../../constants";
import loadPickupTimeFrames from "../../../services/loadPickupTimeFrames";
import type { TPickupTimeFrame } from "../../../types";
import useOrderFieldsLoading from "../../useOrderFieldsLoading";

export type TUpdatePickupTimeFramesParams = {
  countryFrom?: string;
  senderZipCode?: string;
  isLabelPrintByCourier?: boolean;
};

type TUpdatePickupTimeFramesReturn = {
  pickupTimeFrames: TPickupTimeFrame[] | null;
  updatePickupTimeFrames: (params: TUpdatePickupTimeFramesParams) => void;
};

const debouncedPickupTimesLoading = debounce((func) => func(), 200);

const useUpdatePickupTimeFrames = (): TUpdatePickupTimeFramesReturn => {
  const { t } = useTranslation();
  const setMessage = useMessage();

  const [pickupTimeFrames, setPickupTimeFrames] = useState<TPickupTimeFrame[] | null>(null);

  const { setFieldLoading } = useOrderFieldsLoading();

  const { updateNonce, getCurrentNonce } = useNonce();

  const updatePickupTimeFrames = useCallback(
    ({ countryFrom, senderZipCode, isLabelPrintByCourier }: TUpdatePickupTimeFramesParams) => {
      const localNonce = updateNonce();

      if (!countryFrom || !senderZipCode) {
        setPickupTimeFrames([]);
        setFieldLoading(OrderFields.PICKUP_DATE, false);
        return;
      }

      setFieldLoading(OrderFields.PICKUP_DATE, true);

      debouncedPickupTimesLoading(async () => {
        if (localNonce !== getCurrentNonce()) {
          return;
        }
        const result = await loadPickupTimeFrames({ countryFrom, senderZipCode, isLabelPrintByCourier });

        if (!result.isSuccess) {
          setPickupTimeFrames([]);
          setFieldLoading(OrderFields.PICKUP_DATE, false);
          setMessage({
            message: t("order-message-failed_to_load_pickup_timeframes"),
            props: {
              variant: "error",
              icon: <Icon icon="warning" size="1.45rem" />,
              canClose: true,
              padding: "0.45rem 0.75rem",
            },
            zone: ORDER_MESSAGE_ZONE,
          });
          scrollToTheTop();
          return;
        }

        if (!result.data?.length) {
          setPickupTimeFrames([]);
          setFieldLoading(OrderFields.PICKUP_DATE, false);
          setMessage({
            message: t("order-message-no_pickup_timeframes_available"),
            props: {
              variant: "error",
              icon: <Icon icon="warning" size="1.45rem" />,
              canClose: true,
              padding: "0.45rem 0.75rem",
            },
            zone: ORDER_MESSAGE_ZONE,
          });
          scrollToTheTop();
          return;
        }

        setPickupTimeFrames(result.data);
        setFieldLoading(OrderFields.PICKUP_DATE, false);
      });
    },
    [getCurrentNonce, setFieldLoading, setMessage, t, updateNonce]
  );

  return { pickupTimeFrames, updatePickupTimeFrames };
};
export default useUpdatePickupTimeFrames;
